import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Menu, Mountain, User, LogOut, MessageSquare, Search } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadCount } from "@/hooks/useConversations";
import { Badge } from "@/components/ui/badge";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";

const navLinks = [
  { label: "Marketplace", href: "/marketplace" },
  { label: "Buyer Portal", href: "/buyer" },
  { label: "Data Library", href: "/library" },
  { label: "Post a Listing", href: "/post-listing" },
  { label: "AI Analysis", href: "/analysis" },
  { label: "Trucking", href: "/trucking" },
  { label: "Jobs", href: "/jobs" },
  { label: "Manifests", href: "/manifests" },
  { label: "Facilities", href: "/facilities" },
  { label: "Prequalify", href: "/prequalify" },
  { label: "Contact", href: "/contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut, loading } = useAuth();
  const { data: unreadCount } = useUnreadCount();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-primary">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Mountain className="h-8 w-8 text-accent" />
          <span className="font-display text-xl font-bold text-primary-foreground">
            Soil<span className="text-accent">Connect</span> Pro
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent/10 hover:text-accent ${
                location.pathname === link.href
                  ? "text-accent"
                  : "text-primary-foreground/80"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
            className="hidden lg:inline-flex items-center gap-2 rounded-md border border-border/30 bg-primary-foreground/5 px-3 py-1.5 text-xs text-primary-foreground/60 hover:bg-primary-foreground/10 transition-colors"
            aria-label="Search – open command palette"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Search…</span>
            <kbd className="ml-2 rounded bg-primary-foreground/20 px-1.5 py-0.5 text-[10px] font-mono text-primary-foreground">⌘K</kbd>
          </button>
          {!loading && user && <NotificationsBell />}
          {!loading && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-primary-foreground/80 hover:text-accent hover:bg-accent/10 gap-2">
                  <User className="h-4 w-4" />
                  <span className="max-w-[150px] truncate">
                    {profile?.full_name || user.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messages")} className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Messages
                  {(unreadCount ?? 0) > 0 && (
                    <Badge variant="default" className="bg-accent text-accent-foreground text-xs px-1.5 py-0 ml-auto">
                      {unreadCount}
                    </Badge>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : !loading ? (
            <>
              <Button variant="ghost" className="text-primary-foreground/80 hover:text-accent hover:bg-accent/10" asChild>
                <Link to="/login">Log In</Link>
              </Button>
              <Button className="bg-accent text-accent-foreground hover:bg-orange-light" asChild>
                <Link to="/register">Sign Up Free</Link>
              </Button>
            </>
          ) : null}
        </div>

        {/* Mobile nav */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" className="text-primary-foreground" aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-primary border-border/20">
            <nav className="mt-8 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setOpen(false)}
                  className="text-lg font-medium text-primary-foreground/80 hover:text-accent"
                >
                  {link.label}
                </Link>
              ))}
              <hr className="border-border/20" />
              {user ? (
                <>
                   <Link to="/dashboard" onClick={() => setOpen(false)} className="text-lg font-medium text-primary-foreground/80 hover:text-accent">
                    Dashboard
                  </Link>
                  <Link to="/messages" onClick={() => setOpen(false)} className="text-lg font-medium text-primary-foreground/80 hover:text-accent flex items-center gap-2">
                    Messages
                    {(unreadCount ?? 0) > 0 && (
                      <Badge variant="default" className="bg-accent text-accent-foreground text-xs px-1.5 py-0">
                        {unreadCount}
                      </Badge>
                    )}
                  </Link>
                  <button
                    onClick={() => { handleSignOut(); setOpen(false); }}
                    className="text-left text-lg font-medium text-destructive hover:text-destructive/80"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-lg font-medium text-primary-foreground/80 hover:text-accent">
                    Log In
                  </Link>
                  <Button className="bg-accent text-accent-foreground hover:bg-orange-light" asChild>
                    <Link to="/register" onClick={() => setOpen(false)}>Sign Up Free</Link>
                  </Button>
                </>
              )}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
